import { useState, useEffect } from "react";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { useManagedWorkspace } from "@/hooks/useManagedWorkspace";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import { TeamMembersTab } from "@/components/team/TeamMembersTab";
import { InvitationsTab } from "@/components/team/InvitationsTab";
import { RolesTab } from "@/components/team/RolesTab";
import { EmailTemplateTab } from "@/components/team/EmailTemplateTab";
import { TeamRegionalPreferencesTab } from "@/components/team/TeamRegionalPreferencesTab";
import { Users, Link2, Shield, Mail, AlertCircle, Globe } from "lucide-react";
import { useLocation } from "wouter";

export default function TeamSettings() {
  const { currentWorkspace } = useWorkspace();
  const { managedWorkspace } = useManagedWorkspace();
  const [, setLocation] = useLocation();
  const [activeTab, setActiveTab] = useState("members");
  
  // Redirect if user doesn't have workspace management access
  useEffect(() => {
    if (!managedWorkspace) {
      setLocation("/dashboard");
    }
  }, [managedWorkspace, setLocation]);

  if (!managedWorkspace) {
    return (
      <div className="container mx-auto py-6 max-w-4xl">
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-muted-foreground">
              No workspace management access.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // For now, assume current user is owner since we don't have role info in workspace context
  // This should be updated when proper role management is implemented

  return (
    <div className="container mx-auto py-6 max-w-4xl space-y-6">
      <div>
        <h1 className="text-xl sm:text-3xl font-bold">Team Settings</h1>
        <p className="text-muted-foreground">
          Manage your team members, invitations, and roles
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="members" className="flex items-center gap-2">
            <Users className="w-4 h-4" />
            Members
          </TabsTrigger>
          <TabsTrigger value="invitations" className="flex items-center gap-2">
            <Link2 className="w-4 h-4" />
            Invitations
          </TabsTrigger>
          <TabsTrigger value="roles" className="flex items-center gap-2">
            <Shield className="w-4 h-4" />
            Roles
          </TabsTrigger>
          <TabsTrigger value="regional" className="flex items-center gap-2">
            <Globe className="w-4 h-4" />
            Regional
          </TabsTrigger>
          <TabsTrigger value="email" className="flex items-center gap-2">
            <Mail className="w-4 h-4" />
            Email
          </TabsTrigger>
        </TabsList>

        <TabsContent value="members" className="mt-6">
          <TeamMembersTab accountId={currentWorkspace!._id} workspaceId={currentWorkspace!._id} />
        </TabsContent>

        <TabsContent value="invitations" className="mt-6">
          <InvitationsTab workspaceId={currentWorkspace!._id} accountId={currentWorkspace!._id} />
        </TabsContent>

        <TabsContent value="roles" className="mt-6">
          <RolesTab accountId={currentWorkspace!._id} />
        </TabsContent>

        <TabsContent value="regional" className="mt-6">
          <TeamRegionalPreferencesTab teamId={currentWorkspace!._id} />
        </TabsContent>

        <TabsContent value="email" className="mt-6">
          <EmailTemplateTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
